import {
  STANDARD_ZIEL_BRANCH,
  type MetarrCodeArbeitspaketZustand,
  type ReasoningStufe,
} from "@cubic/shared";

const AUTOMATISCH_AKTIV = new Set<MetarrCodeArbeitspaketZustand>([
  "wartet_planung",
  "plant",
  "veroeffentlichung_wartet",
  "commit_erstellt",
]);

const BRANCH_UNGUELTIG = /[\s~^:?*\[\\\]]/;

export function istMetarrCodeArbeitspaketAktiv(zustand: string): boolean {
  return AUTOMATISCH_AKTIV.has(zustand as MetarrCodeArbeitspaketZustand);
}

/**
 * Taugt der Name als Arbeitsbranch?
 *
 * `zielBranch` ist der Zweig, auf den das Paket später zielt — er ist je
 * Projekt gepflegt und darf selbst nie als Arbeitsbranch dienen, sonst
 * schriebe der Lauf in sein eigenes Ziel.
 */
export function istSichererMetarrCodeBranch(
  nameRoh: string,
  zielBranch: string = STANDARD_ZIEL_BRANCH,
): boolean {
  const name = nameRoh.trim();
  const teile = name.split("/");
  return (
    name.length >= 1 &&
    name.length <= 255 &&
    name !== zielBranch.trim() &&
    name !== "@" &&
    !name.startsWith("-") &&
    !name.startsWith("/") &&
    !name.endsWith("/") &&
    !teile.some(
      (teil) =>
        !teil ||
        teil.startsWith(".") ||
        teil.endsWith(".") ||
        teil.endsWith(".lock"),
    ) &&
    !name.includes("..") &&
    !name.includes("@{") &&
    !BRANCH_UNGUELTIG.test(name)
  );
}

function slug(text: string): string {
  return text
    .replace(/ß/g, "ss")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

/**
 * Wörter, die einen Branchnamen länger machen, ohne ihn genauer zu machen.
 *
 * Bewusst nur Artikel, Präpositionen und Konjunktionen — nichts, was ein Thema
 * benennen könnte. Bleibt nach dem Sieb nichts übrig, gilt der ungefilterte
 * Satz weiter; lieber ein füllwortreicher Name als gar keiner.
 *
 * Die Einträge stehen in der Form, die `slug` liefert: Umlaute werden dort
 * zerlegt und der Strich entfällt, aus „für" wird `fur` und nicht `fuer`. Die
 * ausgeschriebenen Formen stehen daneben, weil beide getippt werden.
 */
const FUELLWOERTER = new Set([
  "der", "die", "das", "den", "dem", "des", "ein", "eine", "einen", "einem",
  "einer", "eines", "und", "oder", "aber", "in", "im", "an", "am", "auf",
  "aus", "bei", "fur", "fuer", "mit", "nach", "von", "vom", "zu", "zum",
  "zur", "uber", "ueber", "unter", "vor", "als", "dass", "wenn", "ist",
  "sind", "wird", "werden", "soll", "sollen", "es", "sich", "man",
  "the", "a", "of", "to",
]);

/** Höchstlänge des Thementeils — der Rest wird an einer Wortgrenze gekappt. */
const THEMA_GRENZE = 40;

/**
 * Lesbarer Vorschlag nach der bestehenden Feature-Branch-Konvention. Der
 * Server entscheidet weiterhin verbindlich, ob der Name sicher und frei ist.
 *
 * Gekappt wird an Wortgrenzen, nie mitten im Wort. Vorher stand hier ein
 * blindes `slice(0, 72)`, und aus „… den Zweck des Projekts in einem Satz"
 * wurde `…-des-projekts-in-ei` — ein Wortstummel als Branchname.
 */
export function baueMetarrCodeBranch(
  projektKuerzel: string,
  beschreibung: string,
): string {
  const projekt = slug(projektKuerzel).slice(0, 24) || "code";
  const woerter = slug(beschreibung).split("-").filter(Boolean);
  const tragend = woerter.filter((wort) => !FUELLWOERTER.has(wort));
  const thema = kappeAnWortgrenze(
    tragend.length > 0 ? tragend : woerter,
    THEMA_GRENZE,
  );
  return `feat/${projekt}-${thema || "arbeitspaket"}`;
}

/** Nimmt ganze Wörter, solange sie in die Grenze passen. */
function kappeAnWortgrenze(woerter: string[], grenze: number): string {
  const genommen: string[] = [];
  let laenge = 0;
  for (const wort of woerter) {
    const zusatz = genommen.length === 0 ? wort.length : wort.length + 1;
    if (laenge + zusatz > grenze) break;
    genommen.push(wort);
    laenge += zusatz;
  }
  /* Ein einzelnes überlanges Wort ergäbe sonst gar kein Thema. */
  if (genommen.length === 0 && woerter[0]) return woerter[0].slice(0, grenze);
  return genommen.join("-");
}

export function reasoningStufeName(stufe: ReasoningStufe | string): string {
  switch (stufe) {
    case "none":
      return "Ohne";
    case "low":
      return "Niedrig";
    case "medium":
      return "Mittel";
    case "high":
      return "Hoch";
    case "xhigh":
      return "Sehr hoch";
    case "max":
      return "Maximum";
    default:
      return stufe;
  }
}

export function metarrCodeArbeitspaketFortschritt(zustand: string): number {
  switch (zustand as MetarrCodeArbeitspaketZustand) {
    case "wartet_planung":
      return 8;
    case "plant":
      return 28;
    case "bestaetigung_offen":
      return 50;
    case "veroeffentlichung_wartet":
      return 68;
    case "commit_erstellt":
      return 84;
    case "veroeffentlicht":
    case "fertig_ohne_mr":
    case "abgelehnt":
    case "abgebrochen":
    case "veroeffentlichung_gescheitert":
      return 100;
    case "zu_gross":
    case "konflikt":
    case "gescheitert":
      return 100;
    default:
      return 0;
  }
}
